import { useState, ReactNode } from "react"
import { SEO } from "@/components/SEO"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Loader2, ExternalLink, Copy, Check, TrendingUp, TrendingDown, Minus, Users, Activity, BarChart3, Coins, Layers, Lock, Droplets, LucideIcon, AlertCircle, Grid3X3, ChevronUp, ChevronDown, Trophy, Landmark, Dices } from "lucide-react"
import { formatUnits, formatEther } from "ethers"
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts"
import { toast } from "@/components/ui/use-toast"
import welephantLogo from "@/assets/tokens/welephant.svg"
import stWelephantLogo from "@/assets/tokens/stwelephant.svg"
import elephantLogo from "@/assets/tokens/elephant.svg"
import baseLogo from "@/assets/tokens/base.svg"
import bnbLogo from "@/assets/tokens/bnb.svg"
import busdLogo from "@/assets/tokens/busd.svg"
import wethLogo from "@/assets/tokens/weth.svg"
import { usePoolLiquidity, formatUsdValue, formatTokenAmount, truncateAddress, PoolWithStatus } from "@/hooks/usePoolLiquidity"
import { PoolLiquidityData } from "@/services/poolLiquidityCacheService"
import { useStatsCache, isSectionLoading, hasSectionFailed } from "@/hooks/useStatsCache"
import { useLeaderboard, SortField, SortDirection } from "@/hooks/useLeaderboard"
import { useStatusData } from "@/hooks/useStatusData"
import { SystemStatusBanner, StatusPanel } from "@/components/StatusPanel"
import { cn } from "@/lib/utils"
import { BASE_CHAIN, BNB_CHAIN } from "@/lib/chains"
import { FEATURES } from "@/config/features"
import { formatPrice, formatCompactBNB, formatCompactWelephant, formatCompactCount } from "@/lib/ethersUtils"
import { RaffleDialog } from "@/components/RaffleDialog"

const WELEPHANT_DECIMALS = 9
const ST_WELEPHANT_DECIMALS = 18

// Section header component
interface SectionHeaderProps {
  icon: LucideIcon
  title: string
  loading?: boolean
  failed?: boolean
}

function SectionHeader({ icon: Icon, title, loading, failed }: SectionHeaderProps) {
  return (
    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5" />
      {title}
      {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      {failed && <AlertCircle className="h-4 w-4 text-amber-500" title="Some data failed to load, retrying..." />}
    </h2>
  )
}

// Mini stat card component
interface StatCardProps {
  label: string
  value: ReactNode
  subLabel?: string
  icon?: ReactNode
  loading?: boolean
  className?: string
}

function StatCard({ label, value, subLabel, icon, loading, className }: StatCardProps) {
  return (
    <Card className={cn("", className)}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          {icon}
          {label}
        </div>
        <div className="text-2xl font-bold">
          {loading ? (
            <span className="text-muted-foreground">...</span>
          ) : value ?? "—"}
        </div>
        {subLabel && (
          <div className="text-xs text-muted-foreground">{subLabel}</div>
        )}
      </CardContent>
    </Card>
  )
}

// Large featured stat card (for price/market cap)
interface FeaturedStatCardProps {
  label: string
  value: ReactNode
  icon?: ReactNode
  loading?: boolean
}

function FeaturedStatCard({ label, value, icon, loading }: FeaturedStatCardProps) {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <div className="text-2xl font-bold flex items-center justify-center gap-2">
          {loading ? (
            <span className="text-muted-foreground">...</span>
          ) : (
            <>
              {icon}
              {value ?? "—"}
            </>
          )}
        </div>
        <div className="text-sm text-muted-foreground mt-1">{label}</div>
      </CardContent>
    </Card>
  )
}

type StatsTab = 'general' | 'leaderboard' | 'status'

export function StatsPage() {
  const [activeTab, setActiveTab] = useState<StatsTab>('general')
  const { pools, poolsWithStatus, loading: poolsLoading, failedCount: poolsFailedCount, refetch: refetchPools } = usePoolLiquidity()
  const { cache, isLoading, failedCount, refresh } = useStatsCache()
  const leaderboard = useLeaderboard(FEATURES.mining)
  const statusData = useStatusData(FEATURES.mining && activeTab === 'status')

  // Extract data from cache
  const prices = cache.prices.data
  const baseTokenData = cache.baseTokenData.data
  const bnbTokenData = cache.bnbTokenData.data
  const gameStats = cache.gameCoreStats.data
  const dailyStats = cache.dailyStats.data
  const squareWins = cache.squareWins.data
  const stakingData = cache.stakingData.data

  // Section loading states
  const tokenOverviewLoading = isSectionLoading(cache, 'tokenOverview')
  const tokenOverviewFailed = hasSectionFailed(cache, 'tokenOverview')
  const supplyLoading = isSectionLoading(cache, 'supply')
  const supplyFailed = hasSectionFailed(cache, 'supply')
  const gameLoading = isSectionLoading(cache, 'game')
  const gameFailed = hasSectionFailed(cache, 'game')
  const dailyLoading = isSectionLoading(cache, 'daily')
  const dailyFailed = hasSectionFailed(cache, 'daily')
  const squareWinsLoading = isSectionLoading(cache, 'squareWins')
  const squareWinsFailed = hasSectionFailed(cache, 'squareWins')
  const stakingLoading = isSectionLoading(cache, 'staking')
  const stakingFailed = hasSectionFailed(cache, 'staking')

  const handleRefresh = () => {
    refresh()
    refetchPools()
    if (FEATURES.mining && activeTab === 'leaderboard') {
      leaderboard.refresh()
    }
    if (FEATURES.mining && activeTab === 'status') {
      statusData.refresh()
    }
  }

  // Calculate derived stats
  const totalSupply = baseTokenData?.supply != null && bnbTokenData?.supply != null
    ? baseTokenData.supply + bnbTokenData.supply
    : null

  const priceDelta = prices?.basePriceUsd != null && prices?.bnbPriceUsd != null && prices.bnbPriceUsd > 0
    ? ((prices.basePriceUsd - prices.bnbPriceUsd) / prices.bnbPriceUsd) * 100
    : null

  // Calculate TVL from pools
  const baseTvl = pools
    .filter(p => p.chainId === BASE_CHAIN.id)
    .reduce((sum, p) => sum + (p.totalValueUsd ?? 0), 0)
  const bnbTvl = pools
    .filter(p => p.chainId === BNB_CHAIN.id)
    .reduce((sum, p) => sum + (p.totalValueUsd ?? 0), 0)
  const totalTvl = baseTvl + bnbTvl

  // WELEPHANT price: WelephantSwap contract price when the game stats are
  // loaded, otherwise the BNB Chain market price (the only source when the
  // Mining feature is off and game stats are never fetched)
  const welephantPriceUsd = gameStats?.welephantPriceUsd ?? prices?.bnbPriceUsd ?? null

  const marketCap = welephantPriceUsd != null && totalSupply != null
    ? welephantPriceUsd * parseFloat(formatUnits(totalSupply, WELEPHANT_DECIMALS))
    : null

  // Calculate staking derived stats
  const stakingExchangeRate = stakingData != null && stakingData.totalSupply > 0n
    ? Number(stakingData.totalAssets * BigInt(10 ** 9)) / Number(stakingData.totalSupply)
    : stakingData != null ? 1 : null

  const stakingTvl = welephantPriceUsd != null && stakingData?.totalAssets != null
    ? welephantPriceUsd * parseFloat(formatUnits(stakingData.totalAssets, WELEPHANT_DECIMALS))
    : null

  return (
    <>
      <SEO
        title="Stats"
        url="/stats"
        description={FEATURES.mining
          ? "View WELEPHANT protocol statistics. Track token prices, supply distribution, TVL, game metrics, and daily activity across BNB Chain and Base."
          : "View WELEPHANT protocol statistics. Track token prices, supply distribution, staking, and TVL across BNB Chain and Base."}
        keywords="protocol stats, WELEPHANT analytics, TVL, market cap, DeFi metrics"
      />
      <div className="container py-16">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Protocol Stats</h1>
          <div className="flex items-center gap-2">
            {failedCount > 0 && (
              <span className="text-xs text-amber-500">
                {failedCount} retrying...
              </span>
            )}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 rounded-md hover:bg-muted transition-colors disabled:opacity-50"
              title="Refresh all stats"
            >
              <svg
                className={cn("h-5 w-5", isLoading && "animate-spin")}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as StatsTab)} className="w-full">
          {FEATURES.mining && (
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
              <TabsTrigger value="status">Status</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="general" className="mt-0">
        <div className="space-y-8">
          {/* Prediction Mining */}
          {FEATURES.mining && (
          <section>
            <SectionHeader
              icon={Grid3X3}
              title="Prediction Mining"
              loading={gameLoading}
              failed={gameFailed}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="Total Rounds"
                value={gameStats?.totalRounds?.toString()}
                loading={gameLoading && !gameStats}
              />
              <StatCard
                label="Unique Players"
                value={gameStats?.uniquePlayers?.toString()}
                loading={gameLoading && !gameStats}
              />
              <StatCard
                label="Jackpot"
                value={gameStats?.paydayFund != null ? formatWelephant(gameStats.paydayFund) : null}
                icon={<img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />}
                loading={gameLoading && !gameStats}
              />
              <StatCard
                label="BNB Paid Out"
                value={gameStats?.totalEthPaidOut != null ? formatBnb(gameStats.totalEthPaidOut) : null}
                icon={<img src={bnbLogo} alt="BNB" className="w-4 h-4" />}
                loading={gameLoading && !gameStats}
              />
              <StatCard
                label="WELEPHANT Paid Out"
                value={gameStats?.totalWelephantPaidOut != null ? formatWelephant(gameStats.totalWelephantPaidOut) : null}
                icon={<img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />}
                loading={gameLoading && !gameStats}
              />
              {/* Hidden until more volume — Gas Pool and Vault BNB Pending */}
              <StatCard
                label="Vault Liquidity"
                value={gameStats?.vaultWelephantAvailable != null ? formatWelephant(gameStats.vaultWelephantAvailable) : null}
                icon={<img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />}
                subLabel="Available for claims"
                loading={gameLoading && !gameStats}
              />
            </div>
          </section>
          )}

          {/* Token Overview */}
          <section>
            <SectionHeader
              icon={Coins}
              title="Token Overview"
              loading={tokenOverviewLoading}
              failed={tokenOverviewFailed}
            />

            {/* Featured: Price & Market Cap */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <FeaturedStatCard
                label="WELEPHANT Price"
                value={welephantPriceUsd != null ? formatPrice(welephantPriceUsd) : null}
                icon={<img src={welephantLogo} alt="" className="h-7 w-7" />}
                loading={welephantPriceUsd == null && tokenOverviewLoading}
              />
              <FeaturedStatCard
                label="Market Cap"
                value={marketCap != null ? formatMarketCap(marketCap) : null}
                loading={(welephantPriceUsd == null && tokenOverviewLoading) || (supplyLoading && !totalSupply)}
              />
            </div>

            {/* Price breakdown tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="Base Price"
                value={prices?.basePriceUsd != null ? formatPrice(prices.basePriceUsd) : null}
                icon={<img src={baseLogo} alt="Base" className="w-4 h-4" />}
                loading={cache.prices.status === 'loading'}
              />
              <Card>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground mb-1">Price Delta</div>
                  <div className={cn(
                    "text-2xl font-bold flex items-center gap-1",
                    priceDelta != null && priceDelta > 1 && "text-green-500",
                    priceDelta != null && priceDelta < -1 && "text-red-500"
                  )}>
                    {cache.prices.status === 'loading' ? (
                      <span className="text-muted-foreground">...</span>
                    ) : priceDelta != null ? (
                      <>
                        {priceDelta > 0.5 ? (
                          <TrendingUp className="h-5 w-5" />
                        ) : priceDelta < -0.5 ? (
                          <TrendingDown className="h-5 w-5" />
                        ) : (
                          <Minus className="h-5 w-5" />
                        )}
                        {priceDelta > 0 ? "+" : ""}{priceDelta.toFixed(2)}%
                      </>
                    ) : "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">Base vs BNB</div>
                </CardContent>
              </Card>
              <StatCard
                label="BNB Chain Price"
                value={prices?.bnbPriceUsd != null ? formatPrice(prices.bnbPriceUsd) : null}
                icon={<img src={bnbLogo} alt="BNB" className="w-4 h-4" />}
                loading={cache.prices.status === 'loading'}
              />
            </div>
          </section>

          {/* Supply Distribution */}
          <section>
            <SectionHeader
              icon={Layers}
              title="Supply Distribution"
              loading={supplyLoading}
              failed={supplyFailed}
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Total Supply"
                value={totalSupply != null ? formatSupply(totalSupply) : null}
                icon={<img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />}
                loading={supplyLoading && !totalSupply}
              />
              <StatCard
                label="On Base"
                value={baseTokenData?.supply != null ? formatSupply(baseTokenData.supply) : null}
                icon={<img src={baseLogo} alt="Base" className="w-4 h-4" />}
                loading={cache.baseTokenData.status === 'loading'}
              />
              <StatCard
                label="On BNB Chain"
                value={bnbTokenData?.supply != null ? formatSupply(bnbTokenData.supply) : null}
                icon={<img src={bnbLogo} alt="BNB" className="w-4 h-4" />}
                loading={cache.bnbTokenData.status === 'loading'}
              />
              <StatCard
                label="In Bridge"
                value={baseTokenData?.bridgeEscrow != null && bnbTokenData?.bridgeEscrow != null
                  ? formatSupply(baseTokenData.bridgeEscrow + bnbTokenData.bridgeEscrow)
                  : null}
                icon={<img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />}
                subLabel="Locked in Wormhole"
                loading={supplyLoading && (!baseTokenData || !bnbTokenData)}
              />
            </div>
          </section>

          {/* Staking */}
          <section>
            <SectionHeader
              icon={Landmark}
              title="Staking"
              loading={stakingLoading}
              failed={stakingFailed}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="7d APR"
                value={stakingData != null ? `${stakingData.apr7d.toFixed(2)}%` : null}
                loading={stakingLoading && !stakingData}
              />
              <StatCard
                label="Exchange Rate"
                value={stakingExchangeRate != null ? `${formatStakingRate(stakingExchangeRate)}` : null}
                icon={<img src={stWelephantLogo} alt="stWELEPHANT" className="w-4 h-4" />}
                subLabel="WELEPHANT per stWELEPHANT"
                loading={stakingLoading && !stakingData}
              />
              <StatCard
                label="stWELEPHANT Supply"
                value={stakingData?.totalSupply != null ? formatSupply(stakingData.totalSupply, ST_WELEPHANT_DECIMALS) : null}
                icon={<img src={stWelephantLogo} alt="stWELEPHANT" className="w-4 h-4" />}
                loading={stakingLoading && !stakingData}
              />
              <StatCard
                label="Staking TVL"
                value={stakingTvl != null ? formatMarketCap(stakingTvl) : null}
                loading={(stakingLoading && !stakingData) || (gameLoading && !gameStats)}
              />
              <StatCard
                label="WELEPHANT Staked"
                value={stakingData?.totalAssets != null ? formatSupply(stakingData.totalAssets) : null}
                icon={<img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />}
                loading={stakingLoading && !stakingData}
              />
            </div>

            {/* 30-Day Staking Fees Chart */}
            {stakingData && stakingData.chartData.length > 0 && (
              <Card className="mt-4">
                <CardContent className="p-4">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Staking Fees (30 Days)
                  </h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stakingData.chartData}>
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => `${v.toFixed(0)}`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                          formatter={(value: number) => [`${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} WELEPHANT`, 'Yield']}
                        />
                        <Area
                          type="monotone"
                          dataKey="yield"
                          stroke="#8b5cf6"
                          fill="#8b5cf6"
                          fillOpacity={0.2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </section>

          {/* TVL Summary */}
          <section>
            <SectionHeader icon={Lock} title="Total Value Locked" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="Total TVL"
                value={formatUsdCompact(totalTvl)}
                loading={poolsLoading && pools.length === 0}
              />
              <StatCard
                label="Base TVL"
                value={formatUsdCompact(baseTvl)}
                icon={<img src={baseLogo} alt="Base" className="w-4 h-4" />}
                loading={poolsLoading && pools.length === 0}
              />
              <StatCard
                label="BNB Chain TVL"
                value={formatUsdCompact(bnbTvl)}
                icon={<img src={bnbLogo} alt="BNB" className="w-4 h-4" />}
                loading={poolsLoading && pools.length === 0}
              />
            </div>
          </section>

          {/* Winning Squares Heatmap */}
          {FEATURES.mining && (
          <section>
            <SectionHeader
              icon={Grid3X3}
              title="Winning Squares (Lifetime)"
              loading={squareWinsLoading}
              failed={squareWinsFailed}
            />
            {squareWinsLoading && !squareWins ? (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ) : squareWins ? (
              <Card>
                <CardContent className="p-4">
                  <SquareWinsHeatmap wins={squareWins.wins} />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8 text-muted-foreground">
                    No square wins data available yet
                  </div>
                </CardContent>
              </Card>
            )}
          </section>
          )}

          {/* Daily Activity Stats */}
          {FEATURES.mining && (
          <section>
            <SectionHeader
              icon={Activity}
              title="Daily Activity"
              loading={dailyLoading}
              failed={dailyFailed}
            />

            {/* Time-based aggregated stats as mini-cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard
                label="Active (24h)"
                value={dailyStats?.aggregated.activePlayers24h.toString()}
                icon={<Users className="h-4 w-4" />}
                loading={dailyLoading && !dailyStats}
              />
              <Card>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground mb-1">Plays (24h/7d/30d)</div>
                  <div className="text-xl font-bold">
                    {dailyLoading && !dailyStats ? (
                      <span className="text-muted-foreground">...</span>
                    ) : dailyStats ? (
                      <span className="text-base">
                        {formatCompactCount(dailyStats.aggregated.plays24h)} / {formatCompactCount(dailyStats.aggregated.plays7d)} / {formatCompactCount(dailyStats.aggregated.plays30d)}
                      </span>
                    ) : "—"}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <img src={bnbLogo} alt="BNB" className="w-4 h-4" />
                    Fees (24h/7d/30d)
                  </div>
                  <div className="text-lg font-bold">
                    {dailyLoading && !dailyStats ? (
                      <span className="text-muted-foreground">...</span>
                    ) : dailyStats ? (
                      <span className="text-sm">
                        {formatBnbCompact(dailyStats.aggregated.fees24h)} / {formatBnbCompact(dailyStats.aggregated.fees7d)} / {formatBnbCompact(dailyStats.aggregated.fees30d)}
                      </span>
                    ) : "—"}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />
                    Buybacks (24h/7d/30d)
                  </div>
                  <div className="text-lg font-bold">
                    {dailyLoading && !dailyStats ? (
                      <span className="text-muted-foreground">...</span>
                    ) : dailyStats ? (
                      <span className="text-sm">
                        {formatWelephantCompact(dailyStats.aggregated.buybacks24h)} / {formatWelephantCompact(dailyStats.aggregated.buybacks7d)} / {formatWelephantCompact(dailyStats.aggregated.buybacks30d)}
                      </span>
                    ) : "—"}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts - each in its own card */}
            {dailyLoading && !dailyStats ? (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ) : dailyStats && dailyStats.chartData.length > 0 ? (
              <div className="grid gap-4">
                {/* BNB Fees Chart */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      BNB Fees (30 Days)
                    </h3>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dailyStats.chartData}>
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => `${v.toFixed(2)}`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value: number) => [`${value.toFixed(4)} BNB`, 'Fees']}
                          />
                          <Area
                            type="monotone"
                            dataKey="ethFees"
                            stroke="hsl(var(--primary))"
                            fill="hsl(var(--primary))"
                            fillOpacity={0.2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* WELEPHANT Buybacks Chart */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <img src={welephantLogo} alt="WELEPHANT" className="w-4 h-4" />
                      WELEPHANT Buybacks (30 Days)
                    </h3>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dailyStats.chartData}>
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => formatChartNumber(v)}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value: number) => [formatChartNumber(value), 'Buybacks']}
                          />
                          <Area
                            type="monotone"
                            dataKey="welephantBuybacks"
                            stroke="#f97316"
                            fill="#f97316"
                            fillOpacity={0.2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Daily Plays Chart */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Daily Plays (30 Days)
                    </h3>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dailyStats.chartData}>
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => formatChartNumber(value)}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value: number) => [value.toLocaleString(), 'Plays']}
                          />
                          <Area
                            type="monotone"
                            dataKey="plays"
                            stroke="hsl(var(--primary))"
                            fill="hsl(var(--primary) / 0.2)"
                            strokeWidth={2}
                            name="Plays"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Unique Players Chart */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Unique Players (30 Days)
                    </h3>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dailyStats.chartData}>
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => formatChartNumber(value)}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value: number) => [value.toLocaleString(), 'Players']}
                          />
                          <Area
                            type="monotone"
                            dataKey="uniquePlayers"
                            stroke="#22c55e"
                            fill="rgba(34, 197, 94, 0.2)"
                            strokeWidth={2}
                            name="Unique Players"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8 text-muted-foreground">
                    No daily stats available yet
                  </div>
                </CardContent>
              </Card>
            )}
          </section>
          )}

          {/* Pool Liquidity Detail */}
          <LiquidityPanel
            poolsWithStatus={poolsWithStatus}
            loading={poolsLoading}
            failedCount={poolsFailedCount}
          />
        </div>
          </TabsContent>

          {FEATURES.mining && (
            <TabsContent value="leaderboard" className="mt-0">
              <LeaderboardPanel
                entries={leaderboard.entries}
                loading={leaderboard.loading}
                error={leaderboard.error}
                sortField={leaderboard.sortField}
                sortDirection={leaderboard.sortDirection}
                onSort={leaderboard.setSort}
              />
            </TabsContent>
          )}

          {FEATURES.mining && (
            <TabsContent value="status" className="mt-0">
              <SystemStatusBanner status={statusData.systemStatus} loading={statusData.statusLoading} reason={statusData.data?.haltReason ?? null} />
              <StatusPanel data={statusData.data} loading={statusData.loading} error={statusData.error} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
    </>
  )
}

// Helper functions
function formatSupply(supply: bigint, decimals: number = WELEPHANT_DECIMALS): string {
  const value = parseFloat(formatUnits(supply, decimals))
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K`
  return value.toFixed(2)
}

function formatMarketCap(mc: number): string {
  if (mc >= 1_000_000_000) return `$${(mc / 1_000_000_000).toFixed(2)}B`
  if (mc >= 1_000_000) return `$${(mc / 1_000_000).toFixed(2)}M`
  if (mc >= 1_000) return `$${(mc / 1_000).toFixed(1)}K`
  return `$${mc.toFixed(0)}`
}

function formatUsdCompact(value: number): string {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  if (value >= 1) return `$${value.toFixed(0)}`
  if (value >= 0.01) return `$${value.toFixed(2)}`
  return '<$0.01'
}

function formatStakingRate(rate: number): string {
  if (!isFinite(rate) || isNaN(rate)) return "—"
  if (rate >= 1000) return `${(rate / 1000).toFixed(2)}K`
  if (rate >= 1) return rate.toFixed(4)
  if (rate >= 0.0001) return rate.toFixed(6)
  return rate.toFixed(4)
}

function formatBnb(value: bigint): string {
  const bnb = parseFloat(formatEther(value))
  if (bnb >= 1000) return `${(bnb / 1000).toFixed(2)}K BNB`
  if (bnb >= 1) return `${bnb.toFixed(2)} BNB`
  if (bnb >= 0.001) return `${bnb.toFixed(4)} BNB`
  return `${bnb.toFixed(6)} BNB`
}

function formatWelephant(value: bigint): string {
  const amount = parseFloat(formatUnits(value, WELEPHANT_DECIMALS))
  if (amount >= 1_000_000_000) return `${(amount / 1_000_000_000).toFixed(2)}B`
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(2)}M`
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(2)}K`
  return amount.toFixed(2)
}

function formatBnbCompact(value: bigint): string {
  const bnb = parseFloat(formatEther(value))
  if (bnb >= 1000) return `${(bnb / 1000).toFixed(1)}K`
  if (bnb >= 1) return `${bnb.toFixed(1)}`
  if (bnb >= 0.01) return `${bnb.toFixed(2)}`
  return `${bnb.toFixed(3)}`
}

function formatWelephantCompact(value: bigint): string {
  const amount = parseFloat(formatUnits(value, WELEPHANT_DECIMALS))
  if (amount >= 1_000_000_000) return `${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(0)}K`
  return amount.toFixed(0)
}

function formatChartNumber(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`
  if (value >= 1) return value.toFixed(0)
  return value.toFixed(2)
}

// Square Wins Heatmap Component
interface SquareWinsHeatmapProps {
  wins: bigint[] // 16 elements for squares 1-16 (indices 0-15)
}

// Heat colors by quartile: Gray (lowest) → Green → Yellow → Red (highest)
const HEAT_COLORS = {
  gray: 'rgb(156, 163, 175)',   // gray-400
  green: 'rgb(34, 197, 94)',    // green-500
  yellow: 'rgb(250, 204, 21)',  // yellow-400
  red: 'rgb(239, 68, 68)',      // red-500
}

function SquareWinsHeatmap({ wins }: SquareWinsHeatmapProps) {
  const totalWins = wins.reduce((sum, w) => sum + Number(w), 0)

  // Create sorted list of squares by win count to determine quartile ranking
  const sortedByWins = wins
    .map((w, i) => ({ index: i, wins: Number(w) }))
    .sort((a, b) => a.wins - b.wins) // Sort ascending (lowest first)

  // Create a map of index -> rank (0-15, where 0 is lowest)
  const rankMap = new Map<number, number>()
  sortedByWins.forEach((item, rank) => {
    rankMap.set(item.index, rank)
  })

  // Get color based on quartile rank (4 squares per color)
  const getQuartileColor = (index: number): string => {
    const rank = rankMap.get(index) ?? 0
    if (rank < 4) return HEAT_COLORS.gray    // Bottom 4 (lowest wins)
    if (rank < 8) return HEAT_COLORS.green   // Next 4
    if (rank < 12) return HEAT_COLORS.yellow // Next 4
    return HEAT_COLORS.red                    // Top 4 (highest wins)
  }

  // Get percentage of total wins
  const getPercentage = (winCount: number): string => {
    if (totalWins === 0) return '0%'
    const pct = (winCount / totalWins) * 100
    if (pct === 0) return '0%'
    if (pct < 1) return '<1%'
    return `${pct.toFixed(0)}%`
  }

  // Format count compactly (e.g., 1.2K)
  const formatCount = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count.toString()
  }

  return (
    <div>
      {/* Stats summary */}
      <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
        <span>Total Wins: {totalWins.toLocaleString()}</span>
      </div>

      {/* Heatmap grid - 4x4 for squares 1-16 */}
      <div className="grid grid-cols-4 gap-2 max-w-xs mx-auto">
        {wins.map((winCount, index) => {
          const squareNumber = index + 1 // Squares are 1-16
          const count = Number(winCount)
          const heatColor = getQuartileColor(index)

          return (
            <div
              key={squareNumber}
              className="aspect-square rounded-lg flex items-center justify-center cursor-default relative p-1 bg-muted/50 border-2"
              style={{ borderColor: heatColor }}
            >
              {/* Square number in top left */}
              <span className="absolute top-1 left-1.5 text-[10px] font-medium text-muted-foreground">
                {squareNumber}
              </span>

              {/* Percentage in center */}
              <span
                className="text-base font-bold"
                style={{ color: heatColor }}
              >
                {getPercentage(count)}
              </span>

              {/* Win count in bottom right */}
              <span className="absolute bottom-1 right-1.5 text-[10px] font-medium text-muted-foreground">
                {formatCount(count)}
              </span>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
        <span>Fewer wins</span>
        <div className="flex gap-1">
          {[HEAT_COLORS.gray, HEAT_COLORS.green, HEAT_COLORS.yellow, HEAT_COLORS.red].map((color, i) => (
            <div
              key={i}
              className="w-4 h-4 rounded-sm border-2 bg-muted/50"
              style={{ borderColor: color }}
            />
          ))}
        </div>
        <span>More wins</span>
      </div>
    </div>
  )
}

// Liquidity Panel Component
interface LiquidityPanelProps {
  poolsWithStatus: PoolWithStatus[]
  loading: boolean
  failedCount: number
}

function LiquidityPanel({ poolsWithStatus, loading, failedCount }: LiquidityPanelProps) {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null)

  const copyToClipboard = async (address: string) => {
    await navigator.clipboard.writeText(address)
    setCopiedAddress(address)
    setTimeout(() => setCopiedAddress(null), 2000)
  }

  // Get DEX display name
  const getDexName = (dex: string): string => {
    switch (dex) {
      case 'aerodrome-cl':
        return 'Aerodrome'
      case 'pancakeswap-v3':
        return 'PancakeSwap V3'
      case 'pancakeswap-v2':
        return 'PancakeSwap V2'
      case 'uniswap-v3':
        return 'Uniswap'
      case 'uniswap-v2':
        return 'Uniswap V2'
      default:
        return dex
    }
  }

  // Get the main token (WELEPHANT or ELEPHANT) from pool
  const getMainToken = (pool: PoolLiquidityData) => {
    const isToken0Main = pool.token0Symbol === 'WELEPHANT' || pool.token0Symbol === 'ELEPHANT'
    return {
      symbol: isToken0Main ? pool.token0Symbol : pool.token1Symbol,
      balance: isToken0Main ? pool.token0BalanceFormatted : pool.token1BalanceFormatted,
      valueUsd: isToken0Main ? pool.token0ValueUsd : pool.token1ValueUsd,
      priceUsd: isToken0Main ? pool.token0PriceUsd : pool.token1PriceUsd,
    }
  }

  // Get the paired token (not WELEPHANT/ELEPHANT) from pool
  const getPairedToken = (pool: PoolLiquidityData) => {
    const isToken0Main = pool.token0Symbol === 'WELEPHANT' || pool.token0Symbol === 'ELEPHANT'
    return {
      symbol: isToken0Main ? pool.token1Symbol : pool.token0Symbol,
      balance: isToken0Main ? pool.token1BalanceFormatted : pool.token0BalanceFormatted,
      valueUsd: isToken0Main ? pool.token1ValueUsd : pool.token0ValueUsd,
      priceUsd: isToken0Main ? pool.token1PriceUsd : pool.token0PriceUsd,
    }
  }

  // Get logo for token symbol
  const getTokenLogo = (symbol: string) => {
    switch (symbol) {
      case 'WELEPHANT':
        return welephantLogo
      case 'ELEPHANT':
        return elephantLogo
      case 'WETH':
        return wethLogo
      case 'BUSD':
        return busdLogo
      case 'WBNB':
      case 'USDT':
      case 'USDC':
      default:
        return bnbLogo
    }
  }

  // Get chain logo
  const getChainLogo = (chainId: number) => {
    return chainId === BASE_CHAIN.id ? baseLogo : bnbLogo
  }

  // Get chain name
  const getChainName = (chainId: number) => {
    return chainId === BASE_CHAIN.id ? 'Base' : 'BNB Chain'
  }

  return (
    <section>
      <SectionHeader
        icon={Droplets}
        title="Pool Liquidity"
        loading={loading}
        failed={failedCount > 0}
      />
      <div className="grid md:grid-cols-2 gap-4">
        {poolsWithStatus.map(({ poolId, cacheKey, entry }) => {
          const pool = entry.data
          const isLoading = entry.status === 'loading'
          const isFailed = entry.status === 'failed'

          // Loading state - show skeleton
          if (isLoading && !pool) {
            return (
              <Card key={cacheKey}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <img src={getChainLogo(poolId.chainId)} alt="" className="w-5 h-5" />
                    <span className="font-medium">{getChainName(poolId.chainId)}</span>
                    <span className="text-xs text-muted-foreground">({getDexName(poolId.dex)})</span>
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-auto" />
                  </div>
                  <div className="space-y-2 mb-3">
                    <div className="h-5 bg-muted/50 rounded animate-pulse" />
                    <div className="h-5 bg-muted/50 rounded animate-pulse" />
                  </div>
                  <div className="h-5 bg-muted/50 rounded animate-pulse" />
                </CardContent>
              </Card>
            )
          }

          // Failed state with no cached data
          if (isFailed && !pool) {
            return (
              <Card key={cacheKey}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <img src={getChainLogo(poolId.chainId)} alt="" className="w-5 h-5" />
                    <span className="font-medium">{getChainName(poolId.chainId)}</span>
                    <span className="text-xs text-muted-foreground">({getDexName(poolId.dex)})</span>
                    <AlertCircle className="h-3 w-3 text-amber-500 ml-auto" title="Retrying..." />
                  </div>
                  <div className="py-4 text-center text-muted-foreground text-sm">
                    <span className="text-amber-500">{poolId.tokenSymbol}</span> pool temporarily unavailable
                  </div>
                </CardContent>
              </Card>
            )
          }

          // No data yet (idle state)
          if (!pool) {
            return (
              <Card key={cacheKey}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <img src={getChainLogo(poolId.chainId)} alt="" className="w-5 h-5" />
                    <span className="font-medium">{getChainName(poolId.chainId)}</span>
                    <span className="text-xs text-muted-foreground">({getDexName(poolId.dex)})</span>
                  </div>
                  <div className="py-4 text-center text-muted-foreground text-sm">
                    Loading...
                  </div>
                </CardContent>
              </Card>
            )
          }

          // Loaded state - show full pool card
          return (
            <Card key={cacheKey} className={isFailed ? 'border-amber-500/30' : ''}>
              <CardContent className="p-4">
                {/* Chain Header */}
                <div className="flex items-center gap-2 mb-3">
                  <img src={getChainLogo(pool.chainId)} alt={pool.chainName} className="w-5 h-5" />
                  <span className="font-medium">{pool.chainName}</span>
                  <span className="text-xs text-muted-foreground">({getDexName(pool.dex)})</span>
                  {isLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-auto" />}
                  {isFailed && <AlertCircle className="h-3 w-3 text-amber-500 ml-auto" title="Retrying..." />}
                </div>

                {/* Token Reserves */}
                <div className="space-y-2 mb-3">
                  {(() => {
                    const mainToken = getMainToken(pool)
                    const pairedToken = getPairedToken(pool)
                    return (
                      <>
                        <div className="flex justify-between text-sm">
                          <div className="flex items-center gap-1.5">
                            <img src={getTokenLogo(mainToken.symbol)} alt={mainToken.symbol} className="w-4 h-4" />
                            <span className="text-muted-foreground">{mainToken.symbol}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-medium">{formatTokenAmount(mainToken.balance)}</span>
                            <span className="text-muted-foreground ml-1.5 text-xs">{formatUsdValue(mainToken.valueUsd)}</span>
                          </div>
                        </div>
                        <div className="flex justify-between text-sm">
                          <div className="flex items-center gap-1.5">
                            <img src={getTokenLogo(pairedToken.symbol)} alt={pairedToken.symbol} className="w-4 h-4" />
                            <span className="text-muted-foreground">{pairedToken.symbol}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-medium">{formatTokenAmount(pairedToken.balance)}</span>
                            <span className="text-muted-foreground ml-1.5 text-xs">{formatUsdValue(pairedToken.valueUsd)}</span>
                          </div>
                        </div>
                      </>
                    )
                  })()}
                </div>

                {/* TVL */}
                <div className="flex justify-between text-sm border-t border-muted pt-2 mb-3">
                  <span className="text-muted-foreground">Total Value Locked</span>
                  <span className="font-medium">{formatUsdValue(pool.totalValueUsd)}</span>
                </div>

                {/* Prices */}
                {(() => {
                  const mainToken = getMainToken(pool)
                  const pairedToken = getPairedToken(pool)
                  return (
                    <div className="space-y-1 text-xs text-muted-foreground mb-3">
                      <div className="flex justify-between">
                        <span>{mainToken.symbol} Price</span>
                        <span>{formatPrice(mainToken.priceUsd)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{pairedToken.symbol} Price</span>
                        <span>{formatPrice(pairedToken.priceUsd)}</span>
                      </div>
                    </div>
                  )
                })()}

                {/* Pool Address & Links */}
                <div className="flex items-center justify-between text-xs">
                  <button
                    onClick={() => copyToClipboard(pool.poolAddress)}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="font-mono">{truncateAddress(pool.poolAddress)}</span>
                    {copiedAddress === pool.poolAddress ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <a
                      href={pool.links.explorer}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      title="View on Explorer"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <a
                      href={pool.links.dexscreener}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors text-[10px] font-medium"
                      title="View on Dexscreener"
                    >
                      DEX
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </section>
  )
}

// Leaderboard Panel Component
interface LeaderboardPanelProps {
  entries: {
    rank: number
    address: string
    ethSpent: bigint
    roundsParticipated: bigint
    ethWon: bigint
    welephantWon: bigint
  }[]
  loading: boolean
  error: string | null
  sortField: SortField
  sortDirection: SortDirection
  onSort: (field: SortField) => void
}

function LeaderboardPanel({ entries, loading, error, sortField, sortDirection, onSort }: LeaderboardPanelProps) {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null)
  const [raffleOpen, setRaffleOpen] = useState(false)

  const copyToClipboard = async (address: string) => {
    await navigator.clipboard.writeText(address)
    setCopiedAddress(address)
    setTimeout(() => setCopiedAddress(null), 2000)
    toast({
      title: "Copied",
      description: address,
    })
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronDown className="h-3 w-3 text-muted-foreground/50" />
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="h-3 w-3" />
      : <ChevronDown className="h-3 w-3" />
  }

  const SortableHeader = ({ field, children, className = '' }: { field: SortField; children: ReactNode; className?: string }) => (
    <th
      className={cn(
        "pb-3 pr-2 sm:pr-4 cursor-pointer hover:text-foreground transition-colors select-none",
        className
      )}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  )

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          Top 100 Players by BNB Won
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </h2>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRaffleOpen(true)} title="Raffle">
          <Dices className="h-4 w-4" />
        </Button>
      </div>

      <RaffleDialog entries={entries} open={raffleOpen} onOpenChange={setRaffleOpen} />

      {loading && entries.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8 text-destructive">
              {error}
            </div>
          </CardContent>
        </Card>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8 text-muted-foreground">
              No leaderboard data available yet
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-4">
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <SortableHeader field="rank" className="pl-4 sm:pl-0">Rank</SortableHeader>
                    <th className="pb-3 pr-2 sm:pr-4">Address</th>
                    <SortableHeader field="ethWon">
                      <div className="flex items-center gap-1">
                        <img src={bnbLogo} alt="BNB" className="w-3 h-3" />
                        BNB Won
                      </div>
                    </SortableHeader>
                    <SortableHeader field="welephantWon">
                      <div className="flex items-center gap-1">
                        <img src={welephantLogo} alt="WELEPHANT" className="w-3 h-3" />
                        WELEPHANT
                      </div>
                    </SortableHeader>
                    <SortableHeader field="roundsParticipated" className="pr-4 sm:pr-0">Rounds</SortableHeader>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {entries.map((entry) => (
                    <tr
                      key={entry.address}
                      className="border-b transition-colors hover:bg-muted/50"
                    >
                      {/* Rank */}
                      <td className="py-3 pr-2 sm:pr-4 pl-4 sm:pl-0">
                        <div className={cn(
                          "font-semibold",
                          entry.rank === 1 && "text-yellow-500",
                          entry.rank === 2 && "text-gray-400",
                          entry.rank === 3 && "text-amber-600"
                        )}>
                          #{entry.rank}
                        </div>
                      </td>

                      {/* Address */}
                      <td className="py-3 pr-2 sm:pr-4">
                        <button
                          onClick={() => copyToClipboard(entry.address)}
                          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors font-mono text-xs"
                        >
                          {truncateAddress(entry.address)}
                          {copiedAddress === entry.address ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </button>
                      </td>

                      {/* BNB Won */}
                      <td className="py-3 pr-2 sm:pr-4">
                        <span className="font-medium">{formatCompactBNB(entry.ethWon)}</span>
                      </td>

                      {/* WELEPHANT Won */}
                      <td className="py-3 pr-2 sm:pr-4">
                        <span className="text-muted-foreground">{formatCompactWelephant(entry.welephantWon)}</span>
                      </td>

                      {/* Rounds */}
                      <td className="py-3 pr-4 sm:pr-0">
                        <span className="text-muted-foreground">{entry.roundsParticipated.toString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 text-xs text-muted-foreground text-center">
              Showing {entries.length} player{entries.length !== 1 ? 's' : ''}
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  )
}
